import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Divider, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getIntegrations } from "../../api/integrationsApi";
import { CRM_PRESENTATION, crmRequestError } from "../../helper/crmCatalog";
import HubSpotPushDialog from "./HubSpotPushDialog";

export default function PushToCrmMenu({ workspaceId, isDirty = false, selectedIds = new Set() }) {
  const [anchor, setAnchor] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState("");
  const [session, setSession] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const nextSessionId = useRef(0);
  const activeSession = session?.savedSearchId === workspaceId && !isDirty ? session : null;
  const selectionError = !workspaceId || isDirty ? "Save this search to push to a CRM"
    : selectedIds.size === 0 ? "Select at least one company"
      : selectedIds.size > 500 ? "Select at most 500 companies per push" : "";
  const canResume = activeSession && (["queued", "running"].includes(activeSession.status) || activeSession.uncertain);

  useEffect(() => {
    if (!anchor) return;
    let active = true;
    setCatalog(null);
    setError("");
    getIntegrations().then((value) => { if (active) setCatalog(value); })
      .catch((requestError) => { if (active) setError(crmRequestError(requestError)); });
    return () => { active = false; };
  }, [anchor]);

  const onStateChange = useCallback(({ sessionId, status, exportId, uncertain, confirmationKey }) => {
    setSession((previous) => {
      if (previous?.id !== sessionId || (previous.status === status && previous.exportId === exportId && previous.uncertain === uncertain && previous.confirmationKey === confirmationKey)) return previous;
      return { ...previous, status: status || previous.status, exportId: exportId || previous.exportId, uncertain: uncertain ?? previous.uncertain, confirmationKey };
    });
  }, []);

  const chooseHubSpot = () => {
    setAnchor(null);
    if (!canResume) {
      setSession({ id: ++nextSessionId.current, savedSearchId: workspaceId, companyIds: [...selectedIds], status: "previewed", uncertain: false });
    }
    setDialogOpen(true);
  };

  return (
    <>
      <Tooltip title={selectionError}>
        <span>
          <Button variant="outlined" size="small" endIcon={<KeyboardArrowDownIcon />}
            aria-label="Push to CRM" aria-haspopup="menu" aria-expanded={Boolean(anchor)}
            disabled={Boolean(selectionError) && !canResume} onClick={(event) => setAnchor(event.currentTarget)}
            sx={{ borderRadius: "10px", textTransform: "none" }}>Push to CRM</Button>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} PaperProps={{ sx: { minWidth: 260, maxWidth: "calc(100vw - 32px)" } }}>
        {!catalog && !error && <MenuItem disabled><Typography role="status">Loading integrations…</Typography></MenuItem>}
        {error && <Box sx={{ p: 1.5 }}><Alert severity="error">{error}</Alert></Box>}
        {catalog && !catalog.enabled && <MenuItem disabled>CRM integrations are not available yet</MenuItem>}
        {catalog?.providers.map((provider) => {
          const connected = provider.provider_id === "hubspot" && provider.connection.connected;
          const future = provider.availability !== "available" || provider.provider_id !== "hubspot";
          const canManage = provider.connection.can_manage;
          return (
            <MenuItem key={provider.provider_id} disabled={!catalog.enabled || future || (!connected && !canManage) || (connected && Boolean(selectionError) && !canResume)}
              component={!future && !connected && canManage ? "a" : "li"}
              href={!future && !connected && canManage ? "/settings/integrations" : undefined}
              onClick={connected && !future ? chooseHubSpot : undefined} sx={{ gap: 1.25, whiteSpace: "normal", py: 1.25 }}>
              {CRM_PRESENTATION[provider.provider_id]?.logo && <Box component="img" alt="" src={CRM_PRESENTATION[provider.provider_id].logo} sx={{ width: 22, height: 22, objectFit: "contain" }} />}
              <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{connected ? "Push to HubSpot" : provider.label}{future ? " · Coming soon" : ""}</Typography>
                {!future && <Typography variant="caption" color="text.secondary">
                  {connected ? `Connected by ${provider.connection.connected_by || "your account owner"} · ${provider.connection.portal_name || provider.connection.portal_id}`
                    : canManage ? "Connect or reconnect in Settings" : "Ask your account owner to connect HubSpot"}
                </Typography>}
              </Box>
            </MenuItem>
          );
        })}
        {activeSession && <Divider />}
        {activeSession && <MenuItem onClick={() => { setAnchor(null); setDialogOpen(true); }}>View latest push</MenuItem>}
      </Menu>
      {activeSession && <HubSpotPushDialog key={activeSession.id} sessionId={activeSession.id}
        open={dialogOpen} onClose={() => setDialogOpen(false)} onStateChange={onStateChange}
        savedSearchId={activeSession.savedSearchId} companyIds={activeSession.companyIds} initialExportId={activeSession.exportId}
        initialUncertain={activeSession.uncertain} initialConfirmationKey={activeSession.confirmationKey} />}
    </>
  );
}
